import _ from 'lodash'
import resetStore from '../helpers/reset-store'
import { DEFAULT_CURRENCY_CODE, DEFAULT_CURRENCY_SYMBOL } from '../helpers/currency'
import axios from 'axios';

export const state = () => ({
  openSidebar: false,
  loggedIn: false,
  isFirstLogin: false,
  isBannerDismissed:false,
  session: {},
  offlineNetwork: false,
  newAccount: null,
  totalNews: 0,
  paymentMethods: {},
  previousOrderId: 0,
  orderId: 0,
  isPunchoutSession: false,
  // Catalogue context management
  currentCatalogueId: null,
  searchCatalogueId: null,

  customerDetails: null,
  currentCatalogueScope: null, // 'my', 'full', or 'custom_cat_new'
  adminSettings: null,
})

export const mutations = {
  reset: resetStore(state),
  setOpenSidebar(state, isOpen) {
    state.openSidebar = isOpen
  },
  getOpenSidebar(state) {
    return state.openSidebar
  },
  setLoggedIn(state, loggedIn) {
    state.loggedIn = loggedIn
  },
  setNewAccount(state, value) {
    state.newAccount = value
  },
  setSession(state, data) {
    state.session = data
  },
  setFirstLogin(state, value) {
    state.isFirstLogin = value
  },
  setBannerDismissed(state, value) {
    state.isBannerDismissed = value
  },
  setNetWorkStatus(state, status) {
    state.offlineNetwork = status
  },
  setTotalNews(state, value) {
    state.totalNews = value
  },
  setPreviousOrderId(state, value) {
    state.previousOrderId = value
  },
  setOrderId(state, value) {
    state.orderId = value
  },
  markAskReadArticle(state) {
    state.session.account.totalReadNews++
  },
  setPunchoutSession(state, value) {
    state.isPunchoutSession = value
  },
  setRequirePoNumber(state, value) {
    if (state.session && state.session.setting) {
      state.session.setting = { ...state.session.setting, requirePoNumber: value }
    }
  },
  setSector(state, sector) {
    if (state.session && state.session.account) {
      state.session.account.sector = sector
      // Also update localStorage to keep it in sync
      const sessionData = localStorage.getItem('userSession')
      if (sessionData) {
        const parsed = JSON.parse(sessionData)
        if (parsed.account) {
          parsed.account.sector = sector
          localStorage.setItem('userSession', JSON.stringify(parsed))
        }
      }
    }
  },
  setCurrentCatalogueId(state, catalogueId) {
    state.currentCatalogueId = catalogueId
    // Persist to localStorage so it survives new tab opens (sessionStorage is per-tab)
    if (catalogueId) {
      localStorage.setItem('currentCatalogueId', catalogueId)
    } else {
      localStorage.removeItem('currentCatalogueId')
    }
  },
  setSearchCatalogueId(state, catalogueId) {
    state.searchCatalogueId = catalogueId
  },
  setCurrentCatalogueScope(state, scope) {
    state.currentCatalogueScope = scope
    // Persist to localStorage
    if (scope) {
      localStorage.setItem('currentCatalogueScope', scope)
    } else {
      localStorage.removeItem('currentCatalogueScope')
    }
  },
  clearCatalogueContext(state) {
    state.currentCatalogueId = null
    state.searchCatalogueId = null
    state.currentCatalogueScope = null
    localStorage.removeItem('currentCatalogueId')
    localStorage.removeItem('currentCatalogueScope')
  },
  SET_CUSTOMER_DETAILS(state, details) {
    state.customerDetails = details;
  },
  setAdminSettings(state, settings) {
    state.adminSettings = settings
  }
}

export const actions = {
  async getTotalNews({ commit }) {
    const data = await axios.get('wp/posts', {
      params: {
        categories: 1,
        per_page: 1,
        _embed: true
      }
    })

    commit('setTotalNews', parseInt(data.headers['x-wp-total']))
  },

  async initializeSession({ commit }) {
    // Restore catalogue context from localStorage (independent of auth state)
    const storedCatalogueId = localStorage.getItem('currentCatalogueId')
    if (storedCatalogueId) {
      commit('setCurrentCatalogueId', storedCatalogueId)
    }

    const storedScope = localStorage.getItem('currentCatalogueScope')
    if (storedScope) {
      commit('setCurrentCatalogueScope', storedScope)
    }

    // Only attempt to re-auth if there's a cached session; otherwise stay logged out
    const sessionData = localStorage.getItem('userSession');
    if (!sessionData) {
      return;
    }

    // Validate the cached token against the backend BEFORE rendering any route.
    // /api/session triggers FeathersJS JWT validation and returns fresh session data.
    // If the token is missing/expired, the backend returns 401 and we clear the cache.
    try {
      const { data } = await axios.get('/api/session');
      localStorage.setItem('userSession', JSON.stringify(data));
      commit('setLoggedIn', true);
      commit('setSession', data);
    } catch (e) {
      const status = _.get(e, 'response.status');
      if (status === 401 || status === 403) {
        localStorage.removeItem('userSession');
        commit('setLoggedIn', false);
        commit('setSession', {});
      } else {
        // Network / 5xx — fall back to cached session so the app still works offline-ish
        const parsedSession = JSON.parse(sessionData);
        commit('setLoggedIn', true);
        commit('setSession', parsedSession);
      }
    }
  },
  async updateBanner({commit}){
      commit('setBannerDismissed', true);
},
  async fetchAdminSettings({ commit }) {
    try {
      const { data } = await axios.get('/api/admin-setting')
      const settings = data.data && data.data.length > 0 ? data.data[0] : null
      commit('setAdminSettings', settings)
      return settings
    } catch (error) {
      console.error('Failed to fetch admin settings:', error)
      return null
    }
  },
  setBannerDismissed({ commit }, value) {
    commit('setBannerDismissed', value)
  },
  async refreshSector({ commit }) {
    try {
      const { data } = await axios.get('/api/sector')
      if (data && data.sector !== undefined) {
        commit('setSector', data.sector)
      }
    } catch (error) {
      console.error('Failed to refresh sector data:', error)
    }
  },
  async updateCurrentContact({ dispatch, commit, getters }, contactId) {
    try {
      const currentContact = getters.getSelectedContact

      // Clear basket BEFORE switching customers to prevent cross-contamination
      dispatch('basket/resetBasketStore', null, { root: true })

      const { data } = await axios.post('/api/select-customer', {
        contactId: contactId
      })
      commit('setSession', data)

      // Clear addresses cache when switching contacts
      commit('basket/clearAddresses', null, { root: true })

      // category
      dispatch('category/setCategories', data.categories, { root: true })
      dispatch('category/getCategories', ['my', 'full'], { root: true })
      dispatch('basket/setSavedBasked', data.basket, { root: true })
      dispatch('wishlist/setInitialData', data.wishList, { root: true })
    } catch (e) {
      console.log(e)
      return e
    }
  },

  async fetchCustomerDetails({ commit }, customerId) {
    try {
      const response = await axios.get('/api/customer', {
        params: {
          internalId: customerId
        }
      });

      // Check response.data directly, not response.data.data
      if (response.data && response.data.length > 0) {
        commit('SET_CUSTOMER_DETAILS', response.data[0]);
      } else {
        console.warn('API call for customer details succeeded, but data was empty or in an unexpected format.');
      }
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
    }
  }
}

export const getters = {
  newAccount(state) {
    return state.newAccount
  },
  isFirstLogin(state) {
    return state.isFirstLogin
  },
  getPreviousOrderId(state) {
    return state.previousOrderId
  },
  getOrderId(state) {
    return state.orderId
  },
  unreadNews(state) {
    return state.totalNews - _.get(state, 'session.account.totalReadNews', 0)
  },
  getAccountInfo(state) {
    return _.get(state, 'session.account', {})
  },
  customerDetails: state => state.customerDetails, // --- ADDED ---
  getCustomerSectors(state) {
    return _.get(state, 'session.customerSectors',{})
  },
  getBannerDismissed(state) {
    return state.isBannerDismissed
  },
  getLoggedIn(state) {
    return state.loggedIn
  },
  getOpenSidebar(state) {
    return state.openSidebar
  },
  dashBoardContacts(state) {
    return _.get(state, 'session.contacts', [])
  },
  canPlaceStockOrder(state) {
    if (
        hasPermission('canViewFullCatalogue', state) ||
        hasPermission('canViewMyCatalogue', state) ||
        hasPermission('canViewUserCatalogue', state)
    ) {
      return true
    } else {
      return false
    }
  },
  punchoutOnly(state) {
    if (!state.isPunchoutSession) {
      if (hasPermission('punchoutOnly', state)) {
        return true
      }
    }
    return false
  },
  // Browser Only contact inside a Punch Out session
  browserOnly(state) {
    if (state.isPunchoutSession) {
      if (hasPermission('browserOnly', state)) {
        return true
      }
    }
    return false
  },
  // Backend outcome of the entity filtering for this session type:
  // { contactEligible, hasEligibleEntity, unavailableReason }
  // Defaults to everything available when the backend sent nothing.
  entityAccess(state) {
    return _.get(state, 'session.entityAccess', {
      contactEligible: true,
      hasEligibleEntity: true,
      unavailableReason: null
    })
  },
  browseOnly(state) {
    if (!state.isPunchoutSession) {
      if (hasPermission('browseOnly', state)) {
        return true
      }
    }
    return false
  },
  viewOnly(state) {
    return hasPermission('viewOnly', state) || !!_.get(state, 'session.setting.impersonation.readOnly', false)
  },
  // False for the viewOnly role and for a view only impersonation: neither may commit orders
  canCommitOrders(state, getters) {
    return !getters.viewOnly
  },
  // True only for an admin's view only impersonation, never for the real viewOnly role
  isViewOnlyImpersonation(state) {
    return !!_.get(state, 'session.setting.impersonation.readOnly', false)
  },
  canPlaceManpackOrder(state) {
    return hasPermission('canViewManpack', state)
  },
  canAddSiteUser(state) {
    return hasPermission('addSiteUser', state)
  },
  canViewOrderHistory(state) {
    return hasPermission('viewOrderHistory', state)
  },
  canApproveOrder(state) {
    return (
        hasPermission('orderApproval', state) &&
        _.get(state, 'session.setting.isApprover')
    )
  },
  canPlaceOverBudget(state) {
    return hasPermission('canPlaceOverBudget', state)
  },
  // --- START: NEW GETTER ---
  // This safely accesses the `needsAproval` flag from your backend session data.
  needsAproval: state => _.get(state, 'session.setting.needsAproval', false),
  // Check if user has a supervisor/approver assigned
  hasSupervisor: state => {
    const supervisor = _.get(state, 'session.account.custentity_cbc_contact_supervisor.value')
    return !!supervisor && supervisor !== '' && supervisor !== null
  },
  // --- END: NEW GETTER ---
  customAddressOnly(state) {
    return hasPermission('customAddressOnly', state)
  },
  accessManpack(state) {
    return hasPermission('canViewManpack', state)
  },
  canViewReporting(state) {
    return hasPermission('viewReporting', state)
  },
  canAddManpackUser(state) {
    return hasPermission('addManpackUser', state)
  },
  canChangeStockAddress(state) {
    return hasPermission('dellAddress', state)
  },
  canChangeManpackAddress(state) {
    return hasPermission('indAddress', state)
  },
  canAddAddress(state) {
    return hasPermission('addAddress', state)
  },
  hasPrivateAddressBook(state) {
    // NetSuite field: custentity_smi_my_smi_contact_address_so
    // Value '1' indicates 'MY-SMI Private Address Book'
    return _.get(state, 'session.account.custentity_smi_my_smi_contact_address_so') === '1'
  },
  canViewAllOrder(state) {
    return hasPermission('viewAllOrders', state)
  },
  pricingHidden(state) {
    return hasPermission('pricingHidden', state)
  },
  canAmendOrderDeliveryAddress(state) {
    return hasPermission('amendOrderDeliveryAddress', state)
  },
  isEvolveUser(state) {
    // Treat the user as an admin if either the legacy flag is set OR the
    // new adminRole field exists on the session (backend Phase 1 sets
    // isEvolveUser from !!user.admin_role_id, but we stay defensive here).
    return !!_.get(state, 'session.account.isEvolveUser')
      || !!_.get(state, 'session.account.adminRole')
  },
  isEnhancedUser() {
    return true
  },
  isSuperUser() {
    return true
  },
  adminRole(state) {
    return _.get(state, 'session.account.adminRole', null)
  },
  adminPermissions(state) {
    return _.get(state, 'session.account.adminPermissions', [])
  },
  hasPermission(state, getters) {
    // Returns a function so callers can do hasPermission('view_users'), or
    // hasPermission(['a', 'b']) to check for any one of several keys - used
    // where a single page/route is reachable via more than one permission.
    return (key) => Array.isArray(key)
      ? key.some(k => _.includes(getters.adminPermissions, k))
      : _.includes(getters.adminPermissions, key)
  },
  hasPermissionFullCatalogue(state) {
    return hasPermission('canViewFullCatalogue', state)
  },
  hasPermissionMyCatalogue(state) {
    return hasPermission('canViewMyCatalogue', state)
  },
  hasPermissionUserCatalogue(state) {
    return hasPermission('canViewUserCatalogue', state)
  },
  hasPermissionManpack(state) {
    // Users with private address books cannot place wearerpack orders
    // because NetSuite requires address ID on line level
    const hasPrivateAddressBook = _.get(state, 'session.account.custentity_smi_my_smi_contact_address_so') === '1'
    return hasPermission('canViewManpack', state) && hasPermission('manpackOrders', state) && !hasPrivateAddressBook
  },
  manpackOnly(state) {
    if (
        hasPermission('canViewManpack', state) &&
        !hasPermission('canViewFullCatalogue', state) &&
        !hasPermission('canViewMyCatalogue', state) &&
        !hasPermission('canViewUserCatalogue', state)
    ) {
      return true
    } else {
      return false
    }
  },
  hasPermissionNone(state) {
    if (
        !hasPermission('canViewFullCatalogue', state) &&
        !hasPermission('canViewMyCatalogue', state) &&
        !hasPermission('canViewUserCatalogue', state) &&
        !hasPermission('canViewManpack', state)
    ) {
      return true
    }

    return false
  },
  setting(state) {
    return _.get(state, 'session.setting', {})
  },
    splitDelivery(state) {
    return _.get(state, 'session.setting.splitDelivery', false)
  },
  // Customer currency (session.setting.currency). Absent = GBP/£, as before.
  currency(state) {
    return _.get(state, 'session.setting.currency') || {}
  },
  currencyCode(state, getters) {
    return _.get(getters.currency, 'code') || DEFAULT_CURRENCY_CODE
  },
  currencySymbol(state, getters) {
    return _.get(getters.currency, 'symbol') || DEFAULT_CURRENCY_SYMBOL
  },
  isForeignCurrency(state, getters) {
    return getters.currencyCode !== DEFAULT_CURRENCY_CODE
  },
  // GBP per 1 unit of the customer's currency, or null when unavailable.
  exchangeRate(state, getters) {
    const rate = parseFloat(_.get(getters.currency, 'exchangeRate'))
    return rate > 0 ? rate : null
  },
  costCentres(state) {
    return _.get(state, 'session.costCentres', []).map(item => {
      item.label = item.name
      item.value = item.name
      return item
    })
  },
  offlineNetwork(state) {
    return state.offlineNetwork
  },
  paymentMethods(state) {
    return _.get(state, 'session.account.payment_methods')
  },
  currentUserId(state) {
    return _.get(state, 'session.setting.userId')
  },
  getCustomers(state) {
    return _.get(state, 'session.customers')
  },
  getSelectedContact() {
    return _.get(state, 'session.selectedUser')
  },

  isPunchoutSession(state) {

    return state.isPunchoutSession
  },

  // Admin impersonation (set by the backend when an admin uses /admin/view-as)
  impersonation(state) {
    return _.get(state, 'session.setting.impersonation', null)
  },

  isImpersonating(state) {
    return !!_.get(state, 'session.setting.impersonation.active', false)
  },

  punchoutButtonText(state) {
    return _.get(state, 'session.setting.punchoutButtonText', null)
  },

  punchoutCartReturnedText(state) {
    return _.get(state, 'session.setting.punchoutCartReturnedText', null)
  },

  getCatalogueId(state) {
    // Always return the current page catalogue ID since search catalogue is transferred on navigation
    // If not in state (e.g., opened in new tab), check localStorage
    return state.currentCatalogueId || localStorage.getItem('currentCatalogueId')
  },
  
  getCurrentCatalogueId(state) {
    return state.currentCatalogueId
  },
  
  getSearchCatalogueId(state) {
    return state.searchCatalogueId
  },

  getCurrentCatalogueScope(state) {
    return state.currentCatalogueScope || localStorage.getItem('currentCatalogueScope')
  },

  basketsToApprove(state) {
    return _.get(state, 'session.basketsToApprove', []);
  },

  // Budget-related getters
  userBudgets(state) {
    return _.get(state, 'session.account.budgets', [])
  },
  userBudgetType(state) {
    return _.get(state, 'session.account.budgetType', null)
  },
  hasUserBudget(state) {
    const budgets = _.get(state, 'session.account.budgets', [])
    return budgets.length > 0
  },
  hasPointsBudget(state) {
    // Check if user has points budget (type '2')
    // Handle both string and number types
    const budgetType = _.get(state, 'session.account.budgetType', null)
    return budgetType === '2' || budgetType === 2
  },
  budgetType(state) {
    // Get the user's budget type (1=Monetary, 2=Points, 3=Allocations)
    return _.get(state, 'session.account.budgetType', null)
  },
  budgetsLoaded(state) {
    // Budgets are loaded when session.account exists
    return !!_.get(state, 'session.account')
  },
  pointsMultiplier(state) {
    // Get the customer's points multiplier, default to 0 if not set
    return _.get(state, 'session.account.pointsMultiplier', 0)
  },
  getAdminSettings(state) {
    return state.adminSettings
  }

}

function hasPermission(permission, state) {

  return _.get(state, 'session.account.permissions', []).includes(permission)
}

export default {
  namespaced: true, // optional, if you want namespacing
  state,
  mutations,
  actions,
  getters,
};